import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type { VariantAttribute } from './models/variant-attribute.model';

export class UmgPromiseDateService {
  constructor() {
    dayjs.extend(utc);
    dayjs.extend(timezone);
  }

  canProcess(attributes?: VariantAttribute[]): boolean {
    return (
      !!attributes &&
      attributes.filter(
        (attr) =>
          attr.propertyKey === 'fixed_promise_date' ||
          attr.propertyKey === 'rolling_promise_date' ||
          attr.propertyKey === 'rolling_promise_date_override'
      ).length > 0
    );
  }

  calculatePromiseDates(attributes?: VariantAttribute[]): Record<string, string> {
    const properties: Record<string, string> = {};
    const fixedPromiseDateAttr = attributes?.find(
      (attr) => attr.propertyKey === 'fixed_promise_date'
    );
    const rollingPromiseDateAttr = attributes?.find(
      (attr) => attr.propertyKey === 'rolling_promise_date'
    );
    const rollingPromiseDateOverrideAttr = attributes?.find(
      (attr) => attr.propertyKey === 'rolling_promise_date_override'
    );
    let rollingPromiseDateCount = this.parseNumeric(rollingPromiseDateAttr?.value);
    if (!rollingPromiseDateCount) {
      rollingPromiseDateCount = this.parseNumeric(rollingPromiseDateOverrideAttr?.value);
    }
    if (!fixedPromiseDateAttr && !rollingPromiseDateCount) {
      return properties;
    }
    if (fixedPromiseDateAttr && this.isFixedDateCalcActive(fixedPromiseDateAttr)) {
      const fixedPromiseDate = this.parseDate(fixedPromiseDateAttr);
      if (fixedPromiseDate) {
        const fixedPromiseDateOutput = fixedPromiseDate.format('MMMM D, YYYY');
        properties[`_shipdate`] = fixedPromiseDateOutput;
        properties[`_date-fixed`] = fixedPromiseDateOutput;
        properties[`_date-fixed-unix`] = fixedPromiseDate.toDate().valueOf().toString();
        properties[`_date-rolling`] = '0';
        properties[`_date-rolling-unix`] = '0';
      }
    } else if (
      rollingPromiseDateCount !== null &&
      this.isRollingDateCalcActive(rollingPromiseDateCount)
    ) {
      const rolledPromiseDate = this.rollPromiseDate(rollingPromiseDateCount);
      const rolledPromiseDateOutput = rolledPromiseDate.format('MMMM D, YYYY');
      properties[`_shipdate`] = rolledPromiseDateOutput;
      properties[`_date-fixed`] = '0';
      properties[`_date-fixed-unix`] = '0';
      properties[`_date-rolling`] = rolledPromiseDateOutput;
      properties[`_date-rolling-unix`] = rolledPromiseDate.toDate().valueOf().toString();
    }
    return properties;
  }

  private isFixedDateCalcActive(fixedPromiseDateAttr?: VariantAttribute): boolean {
    if (!fixedPromiseDateAttr) {
      return false;
    }
    const fixedPromiseDate = this.parseDate(fixedPromiseDateAttr);
    return !!fixedPromiseDate && dayjs().isBefore(fixedPromiseDate);
  }

  private isRollingDateCalcActive(rollingPromiseDateCount: number): boolean {
    return !!rollingPromiseDateCount;
  }

  private rollPromiseDate(rollingPromiseDateCount: number): dayjs.Dayjs {
    let rollingPromiseDate = dayjs();
    let i = 0;
    while (i < rollingPromiseDateCount) {
      rollingPromiseDate = rollingPromiseDate.add(1, 'day');
      // Skip counting the weekend
      if (rollingPromiseDate.day() !== 0 && rollingPromiseDate.day() !== 6) {
        i++;
      }
    }
    return rollingPromiseDate;
  }

  private parseDate(attribute?: VariantAttribute): dayjs.Dayjs | null {
    if (!attribute || !attribute.value) {
      return null;
    }
    if (attribute.value.indexOf('-') >= 0) {
      return dayjs(attribute.value, 'YYYY-MM-DD');
    }
    return dayjs(attribute.value, 'MMMM D, YYYY');
  }

  private parseNumeric(value: any): number | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string' && value !== '') {
      const numberValue = parseInt(value, 10);
      if (!Number.isNaN(numberValue)) {
        return numberValue;
      }
    }
    return null;
  }
}
